import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ArrowDownRight, Radar, Waves } from "lucide-react";
import { fadeUp, viewport } from "@/components/anim";

const TOKENS = ["$FRANK", "$PEPE2", "$BOME", "$WIF", "$POPCAT", "$MEW", "$GIGA", "$SLERF", "$BONK", "$MOODENG", "$PNUT", "$CHILLGUY"];
const B58 = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz123456789";

const rand = (a, b) => Math.random() * (b - a) + a;
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const wallet = () => {
  const s = (n) => Array.from({ length: n }, () => pick(B58.split(""))).join("");
  return `${s(4)}…${s(4)}`;
};

const makeTx = () => {
  const side = Math.random() > 0.42 ? "BUY" : "SELL";
  const sol = rand(12, 940);
  return {
    id: Math.random().toString(36).slice(2),
    side,
    token: pick(TOKENS),
    sol: sol.toFixed(1),
    usd: (sol * 168).toLocaleString("en-US", { maximumFractionDigits: 0 }),
    wallet: wallet(),
    whale: sol > 400,
  };
};

export const WhaleFeed = ({ frankImage }) => {
  const [rows, setRows] = useState(() => Array.from({ length: 7 }, makeTx));
  const timer = useRef(null);

  useEffect(() => {
    const tick = () => {
      setRows((prev) => [makeTx(), ...prev].slice(0, 8));
      timer.current = setTimeout(tick, rand(1400, 3200));
    };
    timer.current = setTimeout(tick, 1800);
    return () => clearTimeout(timer.current);
  }, []);

  return (
    <section
      id="whales"
      data-testid="whale-feed-section"
      className="relative py-24 sm:py-32 bg-[#05060A] grid-bg overflow-hidden"
    >
      <div className="absolute inset-0 radial-glow pointer-events-none" />
      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12"
        >
          <div className="max-w-2xl">
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#14F195] flex items-center gap-2">
              <Radar className="w-4 h-4" /> / Live Whale Radar
            </span>
            <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white">
              Watch the smart money move.
            </h2>
          </div>
          <div className="inline-flex items-center gap-2 rounded-full border border-[#14f19533] bg-[#14f1950f] px-4 py-2 self-start md:self-auto">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#14F195] opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#14F195]" />
            </span>
            <span className="font-mono text-[11px] uppercase tracking-wider text-[#14F195]">
              Streaming Solana mempool
            </span>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Featured signal — the FRANK call */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            className="lg:col-span-2 relative rounded-3xl border border-[#14f19533] glass p-4 overflow-hidden"
            style={{ boxShadow: "0 40px 110px -50px rgba(20,241,149,0.5)" }}
          >
            <div className="flex items-center justify-between px-2 pt-1 pb-3">
              <span className="font-mono text-[11px] uppercase tracking-wider text-[#94A3B8] flex items-center gap-2">
                <Waves className="w-4 h-4 text-[#14F195]" /> Called in VIP
              </span>
              <span className="font-display font-black text-[#14F195] text-glow-green">345X</span>
            </div>
            <div className="relative rounded-2xl overflow-hidden border border-[#ffffff12]">
              <img
                data-testid="featured-signal-image"
                src={frankImage}
                alt="$FRANK 345X live signal chart"
                loading="lazy"
                className="w-full h-[420px] object-cover object-top"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#05060A] via-transparent to-transparent" />
              <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                <span className="rounded-full bg-[#05060A]/80 backdrop-blur px-3 py-1.5 font-mono text-xs text-white border border-[#14f19533]">
                  $FRANK · Frank Ocean
                </span>
                <span className="rounded-full bg-[#14F195] px-3 py-1.5 font-mono text-xs font-bold text-[#05060A]">
                  +289X live
                </span>
              </div>
            </div>
          </motion.div>

          {/* Live feed */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            className="lg:col-span-3 rounded-3xl border border-[#ffffff12] glass p-3 sm:p-4"
          >
            <div className="grid grid-cols-[1fr_auto_auto] sm:grid-cols-[auto_1fr_1fr_auto] gap-3 px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-[#64748B] border-b border-[#ffffff0f]">
              <span className="hidden sm:block">Side</span>
              <span>Token</span>
              <span className="text-right">Size</span>
              <span className="text-right">Wallet</span>
            </div>
            <div className="relative">
              <AnimatePresence initial={false}>
                {rows.map((r) => (
                  <motion.div
                    key={r.id}
                    data-testid="whale-row"
                    layout
                    initial={{ opacity: 0, y: -18, backgroundColor: "rgba(20,241,149,0.14)" }}
                    animate={{ opacity: 1, y: 0, backgroundColor: "rgba(0,0,0,0)" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    className="grid grid-cols-[1fr_auto_auto] sm:grid-cols-[auto_1fr_1fr_auto] items-center gap-3 px-3 py-3.5 border-b border-[#ffffff08] rounded-lg"
                  >
                    <span
                      className={`hidden sm:inline-flex items-center gap-1 font-mono text-xs font-bold w-16 ${
                        r.side === "BUY" ? "text-[#14F195]" : "text-[#FF5470]"
                      }`}
                    >
                      {r.side === "BUY" ? (
                        <ArrowUpRight className="w-3.5 h-3.5" />
                      ) : (
                        <ArrowDownRight className="w-3.5 h-3.5" />
                      )}
                      {r.side}
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="font-display font-bold text-white text-sm sm:text-base">{r.token}</span>
                      {r.whale && (
                        <span className="hidden sm:inline font-mono text-[9px] uppercase tracking-wider text-[#FFD700] border border-[#FFD70044] rounded-full px-1.5 py-0.5">
                          🐋 whale
                        </span>
                      )}
                    </span>
                    <span className="text-right">
                      <span
                        className={`font-mono text-sm font-semibold ${
                          r.side === "BUY" ? "text-[#14F195]" : "text-[#FF5470]"
                        }`}
                      >
                        {r.sol} SOL
                      </span>
                      <span className="block font-mono text-[10px] text-[#64748B]">${r.usd}</span>
                    </span>
                    <span className="text-right font-mono text-xs text-[#94A3B8]">{r.wallet}</span>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
